import { Component, ElementRef, Inject, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { ChartOfAccountExpenseGroupTypeName, ChartOfAccountIncomeGroupTypeName, ConfirmationType, GroupNames, Modules, NotificationDetails, NotificationHeader, NotificationTextMessage } from '@app/core/Enum';
import { CommonService, NotificationService } from '@app/core/Services';
import { AccountState, DownloadAnnualReport, GetAccountProductionTrialBalance, GetFundOpeningBalanceBasedOnAPId, GetJournalAccounts, RegenerateAnnualReport, SaveAnnualReportResponse, UploadAnnualReportResponse } from '@app/core/Store';
import { ConfirmationBoxComponent } from '@app/modules/common';
import { Store } from '@ngxs/store';
import { Guid } from 'guid-typescript';
import { NgxSpinnerService } from 'ngx-spinner';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-pdf-preview',
  templateUrl: './pdf-preview.component.html',
  styleUrls: ['./pdf-preview.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom
})
export class PdfPreviewComponent implements OnInit {
  saveAnnualReportResponseId = Guid.EMPTY as unknown as Guid;
  accountingPeriod: Guid;
  htmlContent: any;
  safeHtml: any;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
        public dialog: MatDialog,
        public dialogRef: MatDialogRef<PdfPreviewComponent>,
        private renderer: Renderer2,
        private sanitizer: DomSanitizer,
        private store: Store,
        private spinner: NgxSpinnerService,
        private notify: NotificationService,
        private el: ElementRef,
        private commonService: CommonService
  ) { }

  ngOnInit(): void {
    this.saveAnnualReportResponseId = this.data.saveAnnualReportResponseId;
    this.htmlContent = this.data?.res;
    this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(this.htmlContent);
  }

  onContentClick(event: MouseEvent): void {
    let target = event.target as HTMLElement | null;
    const container = event.currentTarget as HTMLElement;

    // Walk up from the clicked element to find an anchor tag
    while (target && target !== container) {
      if (target.tagName?.toLowerCase() === 'a') {
        const href = target.getAttribute('href');
        if (href && href.startsWith('#')) {
          event.preventDefault();
          event.stopPropagation();

          const id = href.substring(1);
          const targetEl = container.querySelector(`[id="${id}"]`) as HTMLElement;

          if (targetEl) {
            const containerRect = container.getBoundingClientRect();
            const targetRect = targetEl.getBoundingClientRect();
            container.scrollTop = container.scrollTop + (targetRect.top - containerRect.top);
          }
        }
        return;
      }
      target = target.parentElement;
    }
  }
  
  downloaReport(format: string = 'Docx'): void {
      this.spinner.show();
      this.store
        .dispatch(
          new DownloadAnnualReport({
            accountingPeriodId: this.data.accountingPeriod,
            format: format,
          })
        )
        .subscribe();
  }

  onSaveClick(format: string = 'Docx', isConfirmSave: boolean = false): void {
    let htmlContent = this.data?.res;

    let formData: FormData = new FormData();
   

    formData.append('htmlContent',htmlContent);
    this.spinner.show();
    this.store
      .dispatch(new UploadAnnualReportResponse(formData))
      .subscribe((res) => {
        if (res.annualReport.fileUploadResponseModel.length > 0) {
          this.saveReportResponse(res.annualReport.fileUploadResponseModel[0]?.fileUrl, isConfirmSave);
        } else {
          this.spinner.hide();
        }
      });
    }
  
    saveReportResponse(url: any, isConfirmSave: boolean): void {
      const param = {
        id: this.saveAnnualReportResponseId,
        url: url,
       // htmlUrl: htmlUrl
      };
      this.store
        .dispatch(new SaveAnnualReportResponse(param, this.data.accountingPeriod))
        .subscribe((res) => {
          this.spinner.hide();
          this.saveAnnualReportResponseId =
            res.annualReport.saveAnnualReportResponseId;
          if (isConfirmSave) {
            this.dialogRef.close();
          }
          this.dialogRef.close();
        });
    }

  regenerateClick(): void {
    this.dialog
      .open(ConfirmationBoxComponent, {
        data: {
          type: ConfirmationType.Regenerate,
          headerText: NotificationHeader.confirmation,
          detailText: NotificationDetails.regenerateDetailText,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.checkFundExplainBeforeRegenerate();
        }
      });
  }

  checkFundExplainBeforeRegenerate(): void {
    this.spinner.show();
    forkJoin([
      this.store.dispatch(new GetAccountProductionTrialBalance(this.data?.accountingId)),
      this.store.dispatch(new GetJournalAccounts()),
      this.store.dispatch(new GetFundOpeningBalanceBasedOnAPId(this.data?.accountingId)),
    ]).subscribe(
      ([tbRes, accountsRes, fundExplainRes]) => {
        this.spinner.hide();
        const tbData = tbRes.accountProduction.openingBalanceData;
        const accountList: any[] = this.commonService.addGroupNameToListModels(
          this.store.selectSnapshot(AccountState.getJournalAccounts)
        );
        const fundExplainData = fundExplainRes.openingBalance.openingBalanceFundExplainData;

        const tbEntries = tbData?.openingBalanceData || tbData || [];
        if (!Array.isArray(tbEntries) || tbEntries.length === 0) {
          this.proceedWithRegenerate();
          return;
        }

        const hasFundsInTB = tbEntries.some(
          (entry: any) => accountList?.find((acc: any) => acc.id === entry.accountId)?.groupId === GroupNames.Funds
        );

        if (!hasFundsInTB) {
          this.proceedWithRegenerate();
          return;
        }

        if (this.hasFundExplainDifference(tbEntries, accountList, fundExplainData)) {
          this.showFundExplainConfirmation();
        } else {
          this.proceedWithRegenerate();
        }
      },
      () => {
        this.spinner.hide();
        this.proceedWithRegenerate();
      }
    );
  }

  hasFundExplainDifference(tbEntries: any[], accountList: any[], fundExplainData: any[]): boolean {
    let tbIncome = 0;
    let tbExpense = 0;
    let tbGainLoss = 0;

    tbEntries.forEach((x: any) => {
      const account = accountList?.find((acc: any) => acc.id === x.accountId);
      if (!account) return;

      const totalCredit = +(x.endowmentCredit || 0) + +(x.restrictedCredit || 0) + +(x.unrestrictedCredit || 0);
      const totalDebit = +(x.endowmentDebit || 0) + +(x.restrictedDebit || 0) + +(x.unrestrictedDebit || 0);

      if (account.groupId === GroupNames.Income) {
        if (
          account.typeId === ChartOfAccountIncomeGroupTypeName.DonationsAndLegacies ||
          account.typeId === ChartOfAccountIncomeGroupTypeName.CharitableActivities ||
          account.typeId === ChartOfAccountIncomeGroupTypeName.OtherTradingActivities ||
          account.typeId === ChartOfAccountIncomeGroupTypeName.Investments ||
          account.typeId === ChartOfAccountIncomeGroupTypeName.SeparateMaterialItemofIncome ||
          account.typeId === ChartOfAccountIncomeGroupTypeName.Other
        ) {
          tbIncome += (totalCredit - totalDebit);
        } else if (
          account.typeId === ChartOfAccountIncomeGroupTypeName.GainsLossesonRevaluationofFixedAssets ||
          account.typeId === ChartOfAccountIncomeGroupTypeName.OtherGainsLosses
        ) {
          tbGainLoss += (totalCredit - totalDebit);
        }
      } else if (account.groupId === GroupNames.Expense) {
        if (
          account.typeId === ChartOfAccountExpenseGroupTypeName.RaisingFunds ||
          account.typeId === ChartOfAccountExpenseGroupTypeName.CharitableActivities ||
          account.typeId === ChartOfAccountExpenseGroupTypeName.SupportCosts ||
          account.typeId === ChartOfAccountExpenseGroupTypeName.FinanceCosts ||
          account.typeId === ChartOfAccountExpenseGroupTypeName.GovernanceCosts ||
          account.typeId === ChartOfAccountExpenseGroupTypeName.Other
        ) {
          tbExpense += (totalDebit - totalCredit);
        } else if (
          account.typeId === ChartOfAccountExpenseGroupTypeName.GainsLossesonRevaluationofFixedAssets ||
          account.typeId === ChartOfAccountExpenseGroupTypeName.OtherGainsLosses
        ) {
          tbGainLoss -= (totalDebit - totalCredit);
        }
      }
    });

    tbIncome = +tbIncome.toFixed(2);
    tbExpense = +tbExpense.toFixed(2);
    tbGainLoss = +tbGainLoss.toFixed(2);

    // If TB has no income/expense/gainloss accounts, nothing to assign to funds
    if (tbIncome === 0 && tbExpense === 0 && tbGainLoss === 0) {
      return false;
    }

    let explainIncome = 0;
    let explainExpense = 0;
    let explainGainLoss = 0;

    if (fundExplainData && fundExplainData.length > 0) {
      fundExplainData.forEach((item: any) => {
        explainIncome += +(item.income || 0);
        explainExpense += +(item.expenditure || 0);
        explainGainLoss += +(item.netGainLoss || 0);
      });
    }

    explainIncome = +explainIncome.toFixed(2);
    explainExpense = +explainExpense.toFixed(2);
    explainGainLoss = +explainGainLoss.toFixed(2);

    // Check for differences in income/expense/gainloss only
    return (
      tbIncome !== explainIncome ||
      tbExpense !== explainExpense ||
      tbGainLoss !== explainGainLoss
    );
  }

  showFundExplainConfirmation(): void {
    this.dialog
      .open(ConfirmationBoxComponent, {
        data: {
          type: ConfirmationType.FundExplainCheck,
          headerText: NotificationHeader.confirmation,
          detailText: NotificationDetails.fundExplainCheckDetailText,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.proceedWithRegenerate();
        }
      });
  }

  proceedWithRegenerate(): void {
    this.spinner.show();
    this.store
      .dispatch(
        new RegenerateAnnualReport(
          this.data?.accountingId,
          this.data?.regenerateAnnualReportId
        )
      )
      .subscribe(
        (response: any) => {
          this.spinner.hide();
          if (response) {
            this.htmlContent = response?.annualReport?.getContent;
            this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(this.htmlContent);
            this.notify.success(
              NotificationHeader.success,
              NotificationTextMessage.reportRegeneratedSuccessfully
            );
          }
        },
        () => {
          this.spinner.hide();
        }
      );
  }
}
